<script lang="ts">
	import { devices_settings } from 'src/stores/device-runtime/status';
	//Formatters
	import { format } from 'svelte-i18n';
	//Helpers
	import { isLastAPConnection } from '../NetworkComponents/network_helpers';
	import { real_id } from '../../../helpers/interface_helpers';
	import { onSettingsResponse } from '../../../helpers/device_settings_helpers';
	//Services
	import { gen2Request } from '../../../services/gen_2_http_reqs';
	//Stores
	import {
		getDeviceStatus,
		readDeviceStatus,
		readDeviceSettings,
	} from 'src/stores/device-runtime/status-selectors';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	//Design system components
	import { ToggleV2, Checkbox, Button, ButtonV2 } from '@Shelly/shelly-design-system';
	//Components
	import NetworkConnectedStatus from '../NetworkComponents/NetworkConnectedStatus.svelte';
	import NetworkModal from '../NetworkComponents/NetworkModal.svelte';
	import StaticIPAddressForm from '../NetworkComponents/StaticIPAddressForm.svelte';
	import SettingGuide from '../SettingGuide.svelte';
	//Types
	import type { SettingRequest } from '../../../types/requests/settings/setting';
	import type { Device } from '../../../types/Device';
	import { capFirstChar } from '../../../helpers/utilities';

	$: statusStore = getDeviceStatus(id);
	export let device: Device;
	export let isOffline;

	const id = real_id(device.id),
		settings = readDeviceSettings(id),
		ethIP = settings?.eth?.ip || $statusStore?.eth?.ip;

	let enable = settings?.eth?.enable,
		static_ip = settings?.eth?.ipv4mode === 'static',
		staticIpAddressForm: StaticIPAddressForm,
		modal: NetworkModal,
		waitForResponse = false,
		config: {
			enable: boolean;
			ipv4mode: string;
			ip: string | null;
			netmask: string | null;
			gw: string | null;
			nameserver: string | null;
		};

	$: connected = enable && $statusStore?.eth?.ip;

	const connect = async () => {
			waitForResponse = true;
			const res: SettingRequest = await gen2Request(device, 'Eth.SetConfig', {
				config,
			});
			if (res.isok) {
				settings.eth = config;
				devices_settings.update_devices_settings(id, settings);
			}
			onSettingsResponse(res, device);
			waitForResponse = false;
		},
		validate = () => {
			config = {
				enable,
				ipv4mode: 'dhcp',
				ip: null,
				netmask: null,
				gw: null,
				nameserver: null,
			};
			if (enable && static_ip) {
				const staticIpAddress = staticIpAddressForm.validate();
				if (!staticIpAddress) return false;
				config.ipv4mode = 'static';
				config.ip = staticIpAddress.ip;
				config.netmask = staticIpAddress.mask;
				config.gw = staticIpAddress.gw;
				config.nameserver = staticIpAddress.dns;
			}
			modal.open(!enable && isLastAPConnection(id));
		};

	const onToggleChange = () => {
		enable = !enable;
		validate();
	};
</script>

<SettingGuide
	text={$format('settings_guide_ethernet', {
		default: 'Use a wired Ethernet connection instead of, or as well as, Wi-Fi.',
	})}
/>

{#if $showNewLayout}
	<ToggleV2
		size="m"
		class="mb-xs"
		checked={enable}
		on:click={onToggleChange}
		disabled={isOffline}
		id="checkbox-s0id1kp"
	>
		{capFirstChar($format('sub_settings_title_ethernet', { default: 'ethernet' }))}
	</ToggleV2>
	{#if enable}
		<NetworkConnectedStatus {connected} ip={ethIP} />
		<hr class="mb-m mt-s" />

		<!-- Alex: I will check if this message is still relevant to be shown.
If so it should be globally applied to all settings that require a restart -->

		<!-- <Alert
	class="mt-s mb-s text-warning"
	type="warning"
	heading={$format('device_reboot_required', {
		default: 'Device reboot required'
	})}
	isHeadingBold
	message={$format('will_requires_reboot', {
		default: 'Changing these settings requires a device reboot!'
	})}
/> -->

		<StaticIPAddressForm
			showAlert={false}
			withoutBackground
			removeBottomMargin
			removePaddings
			disabledForm={!enable || isOffline}
			settings={{
				ip: ethIP,
				mask: settings?.eth?.netmask,
				gw: settings?.eth?.gw,
				dns: settings?.eth?.nameserver,
			}}
			bind:this={staticIpAddressForm}
			bind:open={static_ip}
		/>
		{#if static_ip}
			<ButtonV2
				class="mt-s-plus"
				size="m"
				width="full"
				on:click={validate}
				loading={waitForResponse}
				disabled={isOffline}
			>
				{$format('button_save', { default: 'Save' })}
			</ButtonV2>
		{/if}
	{/if}
{:else}
	<Checkbox size="l" class="mb-s" bind:checked={enable} disabled={isOffline} id="checkbox-s0id1kp">
		{capFirstChar($format('option_enable', { default: 'Enable' }))}
	</Checkbox>

	<hr class="mb-m" />

	<NetworkConnectedStatus {connected} ip={ethIP} />

	<!-- Alex: I will check if this message is still relevant to be shown.
If so it should be globally applied to all settings that require a restart -->

	<!-- <Alert
	class="mt-s mb-s text-warning"
	type="warning"
	heading={$format('device_reboot_required', {
		default: 'Device reboot required'
	})}
	isHeadingBold
	message={$format('will_requires_reboot', {
		default: 'Changing these settings requires a device reboot!'
	})}
/> -->

	<StaticIPAddressForm
		showAlert={false}
		withoutBackground
		disabledForm={!enable || isOffline}
		settings={{
			ip: ethIP,
			mask: settings?.eth?.netmask,
			gw: settings?.eth?.gw,
			dns: settings?.eth?.nameserver,
		}}
		bind:this={staticIpAddressForm}
		bind:open={static_ip}
	/>

	<Button size="m" width="full" on:click={validate} loading={waitForResponse} disabled={isOffline}>
		{$format('button_save', { default: 'Save' })}
	</Button>
{/if}

<NetworkModal
	bind:this={modal}
	on:confirm={connect}
	{isOffline}
	btnTexts={{
		cancelText: '',
		saveText: enable
			? $format('btn_connect', { default: 'Connect' })
			: $format('label_disconnect', { default: 'Disconnect' }),
	}}
	title={enable
		? $format('connect_to_ethernet_title', {
				default: 'Connect to Ethernet?',
			})
		: $format('disconnect_from_ethernet_title', {
				default: 'Disconnect from Ethernet?',
			})}
>
	{#if enable}
		{$format('connect_to_ethernet_prompt', {
			default: 'Your device will connect to Ethernet. Are you sure you want to proceed?',
		})}
	{:else}
		{$format('disable_ethernet_prompt_message', {
			default: 'Your device will disconnect from Ethernet. Are you sure you want to proceed?',
		})}
	{/if}
</NetworkModal>
